import {getTestProps} from '@benchling/js-test-utils';
import {Button, Group, Modal, Stack, Text, Textarea, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import {getUserMessage} from 'util/Net';

export interface CreateAgentFoundryResult {
  name: string;
  description: string;
}

interface CreateAgentFoundryModalProps {
  opened: boolean;
  onRequestClose: () => void;
  onRequestSubmit: (result: CreateAgentFoundryResult) => void | Promise<void>;
}

export default function CreateAgentFoundryModal({
  opened,
  onRequestClose,
  onRequestSubmit,
}: CreateAgentFoundryModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(): Promise<void> {
    if (!name.trim() || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await onRequestSubmit({name: name.trim(), description: description.trim()});
      onRequestClose();
    } catch (err: unknown) {
      setIsSubmitting(false);
      const serverMessage = getUserMessage(err);
      if (serverMessage) {
        setErrorMessage(serverMessage);
      } else if (err instanceof Error) {
        setErrorMessage(err.message);
      } else {
        throw err;
      }
    }
  }

  return (
    <Modal opened={opened} onClose={onRequestClose} title="New foundry" size="md">
      <Stack spacing="sm">
        {errorMessage != null && (
          <Text role="alert" size="sm" c="red">
            {errorMessage}
          </Text>
        )}
        <TextInput
          label="Name"
          value={name}
          onChange={(e) => {
            setName(e.currentTarget.value);
          }}
          data-autofocus={true}
          required={true}
          {...getTestProps({component: 'CreateAgentFoundryModal', element: 'nameInput'})}
        />
        <Textarea
          label="Description"
          value={description}
          onChange={(e) => {
            setDescription(e.currentTarget.value);
          }}
          minRows={3}
          {...getTestProps({component: 'CreateAgentFoundryModal', element: 'descriptionInput'})}
        />
        <Group position="right">
          <Button variant="tertiary" onClick={onRequestClose}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit}
            {...getTestProps({component: 'CreateAgentFoundryModal', element: 'submitButton'})}
          >
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
